export type ApiError = { error?: string; issues?: unknown };

export class Client {
  constructor(private readonly token: string) {}

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(path, {
      ...init,
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${this.token}`,
        ...(init?.headers ?? {})
      }
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as ApiError;
      throw new Error(body.error ?? `http_${response.status}`);
    }
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }

  logout() { return this.request<void>('/api/auth/logout', { method: 'POST' }); }
  dashboard() { return this.request<DashboardData>('/api/dashboard'); }
  cases() { return this.request<{ cases: CaseOverview[] }>('/api/cases'); }
  caseDetail(id: string) { return this.request<CaseDetail>(`/api/cases/${id}`); }
  scientific() { return this.request<ScientificData>('/api/scientific/status'); }
  pendingReviews() { return this.request<{ items: PendingReview[] }>('/api/scientific/pending-review'); }
  createCase(input: Record<string, unknown>) {
    return this.request<{ id: string }>('/api/cases', { method: 'POST', body: JSON.stringify(input) });
  }
  dispatch(caseId: string, action: string, reason: string) {
    return this.request<{ eventId: string; correlationId: string }>(`/api/cases/${caseId}/integration/dispatch`, {
      method: 'POST', body: JSON.stringify({ action, reason })
    });
  }
  createFinding(caseId: string, input: Record<string, unknown>) {
    return this.request<{ id: string; status: string }>(`/api/cases/${caseId}/findings`, { method: 'POST', body: JSON.stringify(input) });
  }
  createHypothesis(caseId: string, input: Record<string, unknown>) {
    return this.request<{ id: string; status: string }>(`/api/cases/${caseId}/hypotheses`, { method: 'POST', body: JSON.stringify(input) });
  }
  createDiligence(caseId: string, input: Record<string, unknown>) {
    return this.request<{ id: string; authorizationStatus: string }>(`/api/cases/${caseId}/diligences`, { method: 'POST', body: JSON.stringify(input) });
  }
  createDiligenceResult(caseId: string, diligenceId: string, input: Record<string, unknown>) {
    return this.request<{id: string; diligenceId: string}>(`/api/cases/${caseId}/diligences/${diligenceId}/results`, { method: 'POST', body: JSON.stringify(input) });
  }
  benchmarkOutcome(caseId: string) {
    return this.request<{outcome: Record<string, unknown> | null}>(`/api/cold-case/cases/${caseId}/benchmark/outcome`);
  }
  recordBenchmarkOutcome(caseId: string, input: Record<string, unknown>) {
    return this.request<{id: string; status: string}>(`/api/cold-case/cases/${caseId}/benchmark/outcome`, {method: 'POST', body: JSON.stringify(input)});
  }
  createTimelineEvent(caseId: string, input: Record<string, unknown>) {
    return this.request<{ id: string }>(`/api/cold-case/cases/${caseId}/timeline`, { method: 'POST', body: JSON.stringify(input) });
  }
  createEntity(caseId: string, input: Record<string, unknown>) {
    return this.request<{ id: string; reviewStatus: string }>(`/api/cold-case/cases/${caseId}/entities`, { method: 'POST', body: JSON.stringify(input) });
  }
  createRelation(caseId: string, input: Record<string, unknown>) {
    return this.request<{ id: string; status: string }>(`/api/cold-case/cases/${caseId}/relations`, { method: 'POST', body: JSON.stringify(input) });
  }
  createComparison(caseId: string, input: Record<string, unknown>) {
    return this.request<{ id: string; reviewStatus: string }>(`/api/cold-case/cases/${caseId}/comparisons`, { method: 'POST', body: JSON.stringify(input) });
  }
  hypothesisEvidenceMatrix(caseId: string) {
    return this.request<{links: Array<Record<string, unknown>>}>(`/api/cold-case/cases/${caseId}/hypothesis-evidence`);
  }
  reviewHypothesisEvidence(caseId: string, input: Record<string, unknown>) {
    return this.request<{id:string;verdict:string}>(`/api/cold-case/cases/${caseId}/hypothesis-evidence/reviews`,
      {method:'POST',body:JSON.stringify(input)});
  }
  linkHypothesisEvidence(caseId: string, hypothesisId: string, input: Record<string, unknown>) {
    return this.request<{ linked: boolean }>(`/api/cold-case/cases/${caseId}/hypotheses/${hypothesisId}/evidence`, { method: 'POST', body: JSON.stringify(input) });
  }
  async downloadColdCaseReport(caseId: string, filename = 'cold-case-report.md') {
    const response = await fetch(`/api/cold-case/cases/${caseId}/report`, { headers: { authorization: `Bearer ${this.token}` } });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as ApiError;
      throw new Error(body.error ?? `http_${response.status}`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url; anchor.download = filename; document.body.appendChild(anchor); anchor.click(); anchor.remove();
    URL.revokeObjectURL(url);
  }
  runColdCaseReanalysis(caseId: string) {
    return this.request<{ runId: string; stage: string; metrics: Record<string, number> }>(`/api/cold-case/cases/${caseId}/reanalysis`, { method: 'POST', body: '{}' });
  }
  coldCaseProfiles() {
    return this.request<{ profiles: CaseProfile[]; capabilities: Array<Record<string, unknown>> }>('/api/cold-case/profiles');
  }
  instantiateCaseProfile(profileCode: string, input: { cutoffAt: string; title?: string; jurisdiction?: string; sensitivityLevel?: string }) {
    return this.request<{ caseId: string; profileCode: string; benchmarkStatus: string }>(`/api/cold-case/profiles/${profileCode}/instantiate`, {
      method: 'POST', body: JSON.stringify(input)
    });
  }
  coldCaseHub(caseId: string) { return this.request<HubOverview>(`/api/cold-case/cases/${caseId}/hub`); }
  buildColdCaseAgentPlan(caseId: string) {
    return this.request<{ plan: Array<Record<string, unknown>> }>(`/api/cold-case/cases/${caseId}/hub/plan`, { method: 'POST', body: '{}' });
  }
  runColdCaseAgentHub(caseId: string) {
    return this.request<HubRunResult>(`/api/cold-case/cases/${caseId}/hub/run`, { method: 'POST', body: '{}' });
  }
  freezeColdCaseSourceSet(caseId: string) {
    return this.request<BenchmarkProtocolResult>(`/api/cold-case/cases/${caseId}/benchmark/freeze-sources`, { method: 'POST', body: '{}' });
  }
  freezeColdCasePredictions(caseId: string) {
    return this.request<BenchmarkProtocolResult>(`/api/cold-case/cases/${caseId}/benchmark/freeze-predictions`, { method: 'POST', body: '{}' });
  }
  unlockColdCaseOutcome(caseId: string, reason: string) {
    return this.request<BenchmarkProtocolResult>(`/api/cold-case/cases/${caseId}/benchmark/unlock-outcome`, { method: 'POST', body: JSON.stringify({ reason }) });
  }
  createGeospatialObservation(caseId: string, input: Record<string, unknown>) {
    return this.request<{ id: string; reviewStatus: string }>(`/api/cold-case/cases/${caseId}/geospatial`, { method: 'POST', body: JSON.stringify(input) });
  }
  createCommunicationRecord(caseId: string, input: Record<string, unknown>) {
    return this.request<{ id: string; reviewStatus: string }>(`/api/cold-case/cases/${caseId}/communications`, { method: 'POST', body: JSON.stringify(input) });
  }
  createVehicleEvent(caseId: string, input: Record<string, unknown>) {
    return this.request<{ id: string; reviewStatus: string }>(`/api/cold-case/cases/${caseId}/vehicles`, { method: 'POST', body: JSON.stringify(input) });
  }
  createForensicObservation(caseId: string, input: Record<string, unknown>) {
    return this.request<{ id: string; reviewStatus: string }>(`/api/cold-case/cases/${caseId}/forensics`, { method: 'POST', body: JSON.stringify(input) });
  }
  createMultimodalObservation(caseId: string, input: Record<string, unknown>) {
    return this.request<{ id: string; reviewStatus: string; identityInferencePerformed: boolean }>(`/api/cold-case/cases/${caseId}/multimodal`, { method: 'POST', body: JSON.stringify(input) });
  }
  createScenario(caseId: string, input: Record<string, unknown>) {
    return this.request<{ id: string; supportScore: number; contradictionScore: number; evidenceCoverage: number; meaning: string }>(`/api/cold-case/cases/${caseId}/scenarios`, { method: 'POST', body: JSON.stringify(input) });
  }
  createReview(caseId: string, input: Record<string, unknown>) {
    return this.request<{ id: string }>(`/api/cases/${caseId}/reviews`, { method: 'POST', body: JSON.stringify(input) });
  }
  createDecision(caseId: string, input: Record<string, unknown>) {
    return this.request<{ id: string }>(`/api/cases/${caseId}/decisions`, { method: 'POST', body: JSON.stringify(input) });
  }
  async uploadEvidenceFile(caseId: string, file: File, metadata: {
    title: string; evidenceType: string; occurredAt?: string; classification?: string;
  }) {
    const query = new URLSearchParams({
      title: metadata.title,
      evidenceType: metadata.evidenceType,
      filename: file.name,
      mimeType: file.type || 'application/octet-stream',
      classification: metadata.classification ?? 'restricted'
    });
    if (metadata.occurredAt) query.set('occurredAt', metadata.occurredAt);
    const response = await fetch(`/api/cold-case/cases/${caseId}/evidence-file?${query.toString()}`, {
      method: 'POST',
      headers: { authorization: `Bearer ${this.token}`, 'content-type': 'application/octet-stream' },
      body: file
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as ApiError;
      throw new Error(body.error ?? `http_${response.status}`);
    }
    return response.json() as Promise<{
      documentId: string; evidenceId: string; sha256: string; sizeBytes: number; cutoffStatus: string; storageUri: string;
    }>;
  }
}

export async function changePassword(token: string, currentPassword: string, newPassword: string): Promise<void> {
  const response = await fetch('/api/auth/change-password', {
    method: 'POST', headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({ currentPassword, newPassword })
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(detail.error || `http_${response.status}`);
  }
}

export async function login(email: string, password: string) {
  const response = await fetch('/api/auth/login', {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email, password })
  });
  if (!response.ok) throw new Error('invalid_credentials');
  return response.json() as Promise<{ token: string; user: { name: string; email: string; mustChangePassword: boolean } }>;
}



export type CaseProfile = {
  id: string;
  case_code: string;
  display_name: string;
  primary_capacity: string;
  source_policy: string;
  cutoff_required: number;
  prediction_freeze_required: number;
  outcome_locked_until_freeze: number;
  seeded_narrative: number;
  seeded_outcome: number;
  status: string;
  specialized_capability_count: number;
  specialized_capabilities?: unknown;
};

export type HubOverview = {
  profileCatalog: CaseProfile[];
  profile: Record<string, unknown> | null;
  plan: Array<Record<string, unknown>>;
  results: Array<Record<string, unknown>>;
  consensus: Array<Record<string, unknown>>;
  specialized: Record<string, unknown>;
  context: Record<string, unknown>;
};

export type BenchmarkProtocolResult = {
  benchmarkStatus: string;
  sourceSetFrozenAt?: string | null;
  sourceSetSha256?: string | null;
  predictionFrozenAt?: string | null;
  predictionManifestSha256?: string | null;
  outcomeUnlockedAt?: string | null;
  evidenceCount?: number;
  usableEvidenceCount?: number;
  idempotent?: boolean;
};

export type HubRunResult = {
  runId: string;
  coverageScore: number;
  consensusScore: number;
  blockers: string[];
  results: Array<{
    agentCode: string;
    status: string;
    coverageScore: number;
    confidenceScore: number | null;
    output: Record<string, unknown>;
    limitations: string[];
    blockers: string[];
  }>;
  consensus: Record<string, unknown>;
};

export type CaseOverview = {
  id: string; title: string; case_kind: string; origin_module: string; lifecycle_status: string;
  access_stage: string; investigation_stage: string; legal_stage: string; priority: string;
  opened_at: string; stalled_since: string | null; evidence_count: number; finding_count: number;
  hypothesis_count: number; diligence_count: number; human_review_count: number;
  cutoff_at?: string | null;
};

export type DashboardData = {
  totals: { cases: number; pendingReviews: number; openCounterexamples: number; averageIntegrity: number };
  pipeline: Array<Record<string, unknown>>;
  recentCases: CaseOverview[];
};

export type CaseDetail = {
  case: CaseOverview;
  legalNeeds: Array<Record<string, unknown>>;
  caseDocuments: Array<Record<string, unknown>>;
  evidence: Array<Record<string, unknown>>;
  findings: Array<Record<string, unknown>>;
  hypotheses: Array<Record<string, unknown>>;
  diligences: Array<Record<string, unknown>>;
  technicalAnalyses: Array<Record<string, unknown>>;
  humanReviews: Array<Record<string, unknown>>;
  integrationEvents: Array<Record<string, unknown>>;
  processMovements: Array<Record<string, unknown>>;
  timelineEvents: Array<Record<string, unknown>>;
  entities: Array<Record<string, unknown>>;
  graphEdges: Array<Record<string, unknown>>;
  analysisRuns: Array<Record<string, unknown>>;
  modelExecutions: Array<Record<string, unknown>>;
  custodyEvents: Array<Record<string, unknown>>;
  comparisons: Array<Record<string, unknown>>;
};

export type ScientificData = {
  claimStatus: string; universalProof: boolean; fieldValidationCompleted: boolean; independentReplicationCompleted: boolean;
  hypotheses: Array<Record<string, unknown>>; experiments: Array<Record<string, unknown>>;
  metrics: Array<Record<string, unknown>>; counterexamples: Array<Record<string, unknown>>;
  inheritedWarnings: string[];
  compliance: {
    legal: Array<Record<string, unknown>>;
    lgpd: Array<Record<string, unknown>>;
    sources: Array<Record<string, unknown>>;
  };
};

export type PendingReview = {
  target_type: string; target_id: string; case_id: string; title: string; created_at: string;
  risk_or_priority: string; status: string;
};
